/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `eventDateFilters` for the Caintra Connect frontend.
 * @module utils/eventDateFilters
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
/** Non-member user type (`tipoDeUsuario`). */
export const NON_MEMBER_USER_TYPE = "4";

/** Admin user type (`tipoDeUsuario`). */
export const ADMIN_USER_TYPE = "5";

/** Length of an ISO date prefix (`YYYY-MM-DD`). */
export const DATE_PART_ISO_LENGTH = 10;

/** Length of `HH:mm` for HTML time inputs. */
export const TIME_INPUT_HH_MM_LENGTH = 5;

/** Index of the colon separator in `HH:mm`. */
export const TIME_COLON_INDEX = 2;

/** End-of-day hour for business/B2B event end dates. */
export const END_OF_DAY_HOUR = 23;

/** End-of-day minute for business/B2B event end dates. */
export const END_OF_DAY_MINUTE = 59;

/** End-of-day second for business/B2B event end dates. */
export const END_OF_DAY_SECOND = 0;

/** JavaScript `Date` months are zero-indexed. */
export const MONTH_INDEX_OFFSET = 1;

/** Number of `YYYY-MM-DD` components (`year`, `month`, `day`). */
export const ISO_DATE_COMPONENT_COUNT = 3;

/** Radix for `parseInt` when parsing ISO date parts. */
export const RADIX_DECIMAL = 10;

/** Index of the year component in a split `YYYY-MM-DD` string. */
export const ISO_DATE_YEAR_INDEX = 0;

/** Index of the month component in a split `YYYY-MM-DD` string. */
export const ISO_DATE_MONTH_INDEX = 1;

/** Index of the day component in a split `YYYY-MM-DD` string. */
export const ISO_DATE_DAY_INDEX = 2;

/**
 * Parses a calendar date (YYYY-MM-DD or ISO string) as local midnight.
 * @param {string|Date|null|undefined} value
 * @returns {Date|null}
 */
export const toLocalCalendarDay = (value) => {
  if (value == null || value === "") {
    return null;
  }
  if (value instanceof Date) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const datePart = String(value).trim().slice(0, DATE_PART_ISO_LENGTH);
  const [year, month, day] = datePart.split("-").map(Number);
  if (!year || !month || !day) {
    return null;
  }
  return new Date(year, month - MONTH_INDEX_OFFSET, day);
};

/**
 * @param {string|Date|null|undefined} dateValue
 * @param {Date} [reference=new Date()]
 * @returns {boolean}
 */
export const isBeforeCalendarDay = (dateValue, reference = new Date()) => {
  const day = toLocalCalendarDay(dateValue);
  const refDay = toLocalCalendarDay(reference);
  if (!day || !refDay) {
    return false;
  }
  return day < refDay;
};

/**
 * @param {string|Date|null|undefined} dateValue
 * @param {Date} [reference=new Date()]
 * @returns {boolean}
 */
export const isOnOrAfterCalendarDay = (dateValue, reference = new Date()) => {
  return !isBeforeCalendarDay(dateValue, reference);
};

/**
 * Parses `YYYY-MM-DD` as local end-of-day (23:59:00).
 * @param {string} dateString
 * @returns {Date}
 */
export const parseEndOfDayFromIsoDate = (dateString) => {
  const dateParts = dateString.split("-");
  return new Date(
    parseInt(dateParts[ISO_DATE_YEAR_INDEX], RADIX_DECIMAL),
    parseInt(dateParts[ISO_DATE_MONTH_INDEX], RADIX_DECIMAL) -
      MONTH_INDEX_OFFSET,
    parseInt(dateParts[ISO_DATE_DAY_INDEX], RADIX_DECIMAL),
    END_OF_DAY_HOUR,
    END_OF_DAY_MINUTE,
    END_OF_DAY_SECOND,
  );
};

/**
 * Whether the user may see/register (calendar-day availability dates).
 * @param {object} event
 * @param {object|null|undefined} user
 * @param {Date} [reference=new Date()]
 * @returns {boolean}
 */
export const isEventAvailableForUserByCalendarDay = (
  event,
  user,
  reference = new Date(),
) => {
  const availabilityField =
    user?.tipoDeUsuario === NON_MEMBER_USER_TYPE
      ? event.fechaDisponibleNoSocios
      : event.fechaDisponibleSocios;

  if (availabilityField) {
    return isOnOrAfterCalendarDay(reference, availabilityField);
  }
  return true;
};

/**
 * Whether the user may see/register (datetime availability dates).
 * @param {object} event
 * @param {object|null|undefined} user
 * @param {Date} [reference=new Date()]
 * @returns {boolean}
 */
export const isEventAvailableForUserByDateTime = (
  event,
  user,
  reference = new Date(),
) => {
  const availabilityField =
    user?.tipoDeUsuario === NON_MEMBER_USER_TYPE
      ? event.fechaDisponibleNoSocios
      : event.fechaDisponibleSocios;

  if (availabilityField) {
    const availableDate = new Date(availabilityField);
    return reference >= availableDate;
  }
  return true;
};

/**
 * Filters DynamoDB-style events for the public Events list (virtual, breakfast).
 * @param {Array<object>} events
 * @param {object|null|undefined} user
 * @param {Date} [reference=new Date()]
 * @returns {Array<object>}
 */
export const filterCalendarDayEventsForEventsList = (
  events,
  user,
  reference = new Date(),
) => {
  const active = events.filter((event) => event.activo === true);
  return active.filter((event) => {
    if (isBeforeCalendarDay(event.fechaEvento, reference)) {
      return false;
    }
    return isEventAvailableForUserByCalendarDay(event, user, reference);
  });
};

/**
 * Whether a registration-style event is visible (datetime `fechaEvento` comparison).
 * @param {object} event
 * @param {object|null|undefined} user
 * @param {Date} [reference=new Date()]
 * @param {{ includePast?: boolean }} [options]
 * @returns {boolean}
 */
export const isRegistrationEventVisible = (
  event,
  user,
  reference = new Date(),
  options = {},
) => {
  const eventDate = new Date(event.fechaEvento);
  if (!options.includePast && eventDate < reference) {
    return false;
  }
  return isEventAvailableForUserByDateTime(event, user, reference);
};

/**
 * Whether a business/B2B event is visible (end-of-day `fechaFinEvento`).
 * @param {object} event
 * @param {object|null|undefined} user
 * @param {Date} [reference=new Date()]
 * @param {{ includePast?: boolean }} [options]
 * @returns {boolean}
 */
export const isBusinessStyleEventVisible = (
  event,
  user,
  reference = new Date(),
  options = {},
) => {
  const eventDate = parseEndOfDayFromIsoDate(event.fechaFinEvento);
  if (!options.includePast && eventDate < reference) {
    return false;
  }
  return isEventAvailableForUserByDateTime(event, user, reference);
};

/**
 * B2B management list: future end date only (no availability gating).
 * @param {object} event
 * @param {Date} [reference=new Date()]
 * @param {{ includePast?: boolean }} [options]
 * @returns {boolean}
 */
export const isB2BManagementEventVisible = (
  event,
  reference = new Date(),
  options = {},
) => {
  if (options.includePast) {
    return true;
  }
  const eventDate = parseEndOfDayFromIsoDate(event.fechaFinEvento);
  return eventDate >= reference;
};

/**
 * Active B2B list: keeps events without a parseable `fechaFinEvento`.
 * @param {object} event
 * @param {Date} [reference=new Date()]
 * @param {{ includePast?: boolean }} [options]
 * @returns {boolean}
 */
export const isActiveB2BEventVisible = (
  event,
  reference = new Date(),
  options = {},
) => {
  if (options.includePast) {
    return true;
  }
  const dateParts = event.fechaFinEvento?.split("-");
  if (!dateParts || dateParts.length < ISO_DATE_COMPONENT_COUNT) {
    return true;
  }
  const eventDate = parseEndOfDayFromIsoDate(event.fechaFinEvento);
  return eventDate >= reference;
};

/**
 * @param {Array<object>} events
 * @param {object|null|undefined} user
 * @param {Date} [reference=new Date()]
 * @param {{ includePast?: boolean }} [options]
 * @returns {Array<object>}
 */
export const filterBusinessStyleEventsForUserList = (
  events,
  user,
  reference = new Date(),
  options = {},
) =>
  events.filter((event) =>
    isBusinessStyleEventVisible(event, user, reference, options),
  );
